import { useAttachmentUpload } from '@/helpers/useAttachmentUpload'
import { useEffect, useRef, useState } from 'react'
import { Button } from '../base/button'
import { AttachmentAttributes, AttachmentResourceType } from '@/types'
import { Query } from './collection_query'
import { DocumentNode } from '@apollo/client'
import { fileExtension, imageExtensions } from '@/helpers/file_extension'
import { DocumentIcon } from '@heroicons/react/24/solid'

interface Props {
  defaultValues: Partial<AttachmentAttributes>
  onSuccess: () => void
  refetchQueries?: DocumentNode[]
}

type Status = 'pending' | 'uploading' | 'done'

export const Form = ({ defaultValues, onSuccess, refetchQueries = [] }: Props) => {
  const [attachments, setAttachments] = useState<Record<string, string | ArrayBuffer | null>>({})
  const [status, setStatus] = useState<Status>('pending')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { upload, progress } = useAttachmentUpload({
    key: defaultValues.key || 'images',
    resourceId: defaultValues.resourceId || '',
    resourceType: defaultValues.resourceType || AttachmentResourceType.Event,
    refetchQueries: [...refetchQueries, Query]
  })

  useEffect(() => {
    if (Object.keys(progress).length == 0) return

    Object.entries(progress).forEach(([, progress]) => {
      if (progress.percentage != 100) return
      if (progress.mutationStarted == false) return
      if (progress.mutationLoading == true) return
      if (progress.errors != undefined) return
    })
    setStatus('done')
  }, [progress])

  useEffect(() => {
    if (status == 'done') onSuccess()
  }, [status, onSuccess])

  const preview = () => {
    if (!fileInputRef.current?.files) return

    Array.from(fileInputRef.current?.files).forEach(file => {
      if (imageExtensions.includes(fileExtension(file.name))) {
        const fr = new FileReader
        fr.onload = () => setAttachments(attachments => ({ ...attachments, [file.name]: fr.result }))
        fr.readAsDataURL(file)
      } else {
        setAttachments(attachments => ({ ...attachments, [file.name]: 'file' }))
      }
    })
  }

  const go = () => {
    if (!fileInputRef.current?.files) return

    const files = Array.from(fileInputRef.current?.files)
    if (files.length != 0) {
      setStatus('uploading')
      upload(files)
    }
  }

  return (
    <>
      <div className="flex flex-row gap-6 items-center">
        <input
          ref={fileInputRef}
          className="grow bg-zinc-200 dark:bg-zinc-800 border border-white/10 p-2 rounded-md"
          type="file"
          multiple
          onChange={preview}
        />
        <Button color="green" disabled={status == 'uploading'} onClick={go}>Add</Button>
      </div>
      <div className="flex flex-row gap-4 pt-4 flex-wrap">
        { Object.entries(attachments).map(([filename, dataUrl]) => (
          <div key={filename} className="rounded-md bg-zinc-200 dark:bg-zinc-800 border border-white/10 p-2">
            { dataUrl == 'file'
              ? <DocumentIcon className="w-24 h-24" />
              : <img className="object-contain size-32" src={dataUrl?.toString()} /> }
            <div className="w-full bg-zinc-300 dark:bg-zinc-700 rounded-md px-1">
              <p
                className="bg-green-500 text-white"
                style={{ width: `${progress[filename]?.percentage || 0}%` }}
              >
                {filename}
              </p>
            </div>
          </div>
        )) }
      </div>
    </>
  )
}
